import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";

function CategoryGrid({ categories, basePath }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((c) => (
        <Link
          key={c.id}
          to={`${basePath}/${c.slug}`}
          data-testid={`category-card-${c.slug}`}
          className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-900"
        >
          <div className="aspect-[16/10] overflow-hidden">
            {c.image_url && <img src={fileUrl(c.image_url)} alt={c.name} className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-105" />}
          </div>
          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent p-6">
            <h3 className="font-display text-xl font-bold text-white">{c.name}</h3>
            <p className="mt-1 text-sm text-slate-300">{c.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="rounded-full bg-brand px-3 py-1 text-xs font-bold text-white">{c.machine_count} Listings</span>
              <ArrowRight className="text-white transition-transform group-hover:translate-x-1" size={18} />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function CategoriesPage() {
  const [machine, setMachine] = useState([]);
  const [spare, setSpare] = useState([]);
  useEffect(() => {
    api.get("/categories", { params: { type: "machine" } }).then((r) => setMachine(r.data)).catch(() => {});
    api.get("/categories", { params: { type: "spare" } }).then((r) => setSpare(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <Seo title="Machine & Spare Part Categories | Mashiniya" description="Browse machinery categories — construction, agricultural, drilling, industrial and earthmoving machines — plus engine, hydraulic and other spare-part categories on Mashiniya." canonical={window.location.origin + "/categories"} />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Browse by type</p>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Categories</h1>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-end gap-3">
          <h2 className="font-display text-2xl font-bold text-slate-900">Machine Categories</h2>
          <span className="mb-1 text-sm text-slate-400">New &amp; Used machines</span>
        </div>
        <CategoryGrid categories={machine} basePath="/used-machines" />

        {spare.length > 0 && (
          <>
            <div className="mb-6 mt-14 flex items-end gap-3">
              <h2 className="font-display text-2xl font-bold text-slate-900">Spare Part Categories</h2>
              <span className="mb-1 text-sm text-slate-400">Components &amp; accessories</span>
            </div>
            <CategoryGrid categories={spare} basePath="/spare-parts" />
          </>
        )}
      </div>
    </Layout>
  );
}
